import { useContext } from "react";
import { useAgentRuns, useOrganizationAgentRuns } from "@api/agentRun.api";
import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import {
    useBusyClosesDemoRunForClose,
    useBusyClosesDemoRunForTask,
} from "@components/demo/busyClosesDemo/useBusyClosesDemoLive";
import {
    AgentRunStatusKind,
    getAgentRunStatusKind,
} from "@components/mainInterface/chat/agent/agentRunStatus";
import { ScopeContext, useScope } from "@state/scopeContext.lib";

// The indicator stays up through "Pausing…" because the segment is still
// executing; it drops on Paused and on any finished kind.
export const WORKING_KINDS: ReadonlySet<AgentRunStatusKind> = new Set([
    "running",
    "pausing",
]);

export const isAgentWorking = (run: AgentRunSummary | null): boolean =>
    run !== null && WORKING_KINDS.has(getAgentRunStatusKind(run));

/**
 * The runs of the scoped workspace: the app workspace's own list (the one the
 * chat drawer keeps live), or on an organization page the organization list
 * narrowed to the card's sub-scoped workspace. Both lists are newest first.
 */
function useScopedWorkspaceRuns(): AgentRunSummary[] {
    const root = useContext(ScopeContext);
    const { workspaceId } = useScope();
    const isInAppWorkspace = !!workspaceId && workspaceId === root.workspaceId;
    const isInOrganization = !!root.organizationId && !root.workspaceId;
    const { data: workspaceList } = useAgentRuns({ enabled: isInAppWorkspace });
    const { data: organizationList } = useOrganizationAgentRuns({
        enabled: isInOrganization,
    });
    if (isInAppWorkspace) {
        return workspaceList?.runs ?? [];
    }
    if (isInOrganization) {
        return (organizationList?.runs ?? []).filter(
            (run) => run.workspaceId === workspaceId,
        );
    }
    return [];
}

/** The newest run whose run task is `taskId`, and whether it is executing a segment. */
export const useAgentRunForTask = (
    taskId: string,
): { run: AgentRunSummary | null; isWorking: boolean } => {
    const runs = useScopedWorkspaceRuns();
    const demoRun = useBusyClosesDemoRunForTask(taskId);
    const run =
        demoRun ??
        runs.find((candidate) => candidate.runTaskId === taskId) ??
        null;
    return { run, isWorking: isAgentWorking(run) };
};

interface AgentRunForCloseParams {
    /** The raw close uuid (not the row's `close:` prefixed id). */
    closeId: string | null;
    workspaceId: string;
}

/** The newest organization-list run on a client's close; `isLoading` while the list first loads. */
export const useAgentRunForClose = ({
    closeId,
    workspaceId,
}: AgentRunForCloseParams): {
    run: AgentRunSummary | null;
    isLoading: boolean;
} => {
    const { data, isLoading } = useOrganizationAgentRuns();
    const demoRun = useBusyClosesDemoRunForClose(closeId);
    if (demoRun !== null) {
        return { run: demoRun, isLoading: false };
    }
    const run =
        closeId === null
            ? null
            : ((data?.runs ?? []).find(
                  (candidate) =>
                      candidate.workspaceId === workspaceId &&
                      candidate.close?.id === closeId,
              ) ?? null);
    return { run, isLoading };
};
